import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useBoolean } from '@fluentui/react-hooks';

export interface IFullscreenContext {
  isFullScreen: boolean;
  toggleFullScreen: () => void;
}

interface IProviderProps {
  children: ReactNode;
}

export const FullscreenContext = createContext<IFullscreenContext>({
  isFullScreen: false,
  toggleFullScreen: () => undefined,
});

export const FullscreenContextProvider = ({ children }: IProviderProps) => {
  const [isFullScreen, { toggle: toggleFullScreen }] = useBoolean(false);

  const contextValue: IFullscreenContext = useMemo(
    () => ({
      isFullScreen,
      toggleFullScreen,
    }),
    [isFullScreen, toggleFullScreen],
  );

  return (
    <FullscreenContext.Provider value={contextValue}>
      {children}
    </FullscreenContext.Provider>
  );
};

export const useFullscreenContext = () => useContext(FullscreenContext);

export function withFullscreenContext<T extends object>(
  WrappedComponent: React.ComponentType<T>,
): React.FC<T> {
  // displayName is used for React Dev Tools.
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithContext = (props: T) => {
    return (
      <FullscreenContextProvider>
        <WrappedComponent {...props} />
      </FullscreenContextProvider>
    );
  };

  ComponentWithContext.displayName = `withFullscreenContext(${displayName})`;
  return ComponentWithContext;
}
